import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import ErrorMessage from "../../common/ErrorMessage/ErrorMessage";
import { ValidationResult } from "joi";
import {
  NewMaintenanceTaskInput,
  GetMaintenanceTaskDetailsQuery,
  TripAssetTypes,
  Scalars,
  MaintenanceTaskRecurrenceFrequency,
} from "../../../graphql/generated";
import { isArray, mergeWith, omit, omitBy } from "lodash";
import maintenanceTaskSchema from "./maintenanceTaskSchema";
import useConfirmBeforeLeave from "../../../utils/hooks/useConfirmBeforeLeave";
import { FormDivider } from "../../asset-management/BusinessEntityForm/BusinessEntityForm";
import { useTranslation } from "react-i18next";
import { DateTimePicker } from "@mui/x-date-pickers";
import EnumSelect from "../../common/EnumSelect";
import NumberTextField from "../../common/NumberTextField";
import MileageInput from "../../accounting/BillingRuleForm/value-selectors/MileageInput";
import i18next from "i18next";
import { testIds } from "./MaintenanceTaskForm.testIds";

const METERS_PER_MILE = 1609.34; // Number of meters in a mile

type MaintenanceTask = GetMaintenanceTaskDetailsQuery["maintenanceTaskById"];

enum MaintenanceTaskType {
  MILEAGE = "MILEAGE",
  DATE = "DATE",
}

export type MaintenanceTaskFormProps = {
  assetType: TripAssetTypes;
  assetId: Scalars["ObjectId"];
  initialMaintenanceTask?: MaintenanceTask;
  saving: boolean;
  onSave: (maintenanceTask: NewMaintenanceTaskInput) => void;
};
type PartialMaintenanceTask = Partial<MaintenanceTask>;
type DeepPartialMaintenanceTask = {
  [key in keyof PartialMaintenanceTask]: Partial<PartialMaintenanceTask[key]>;
};

const MaintenanceTaskForm = ({
  initialMaintenanceTask,
  assetType,
  assetId,
  saving,
  onSave,
}: MaintenanceTaskFormProps) => {
  const { t } = useTranslation(["users", "common"]);
  const [validationResult, setValidationResult] =
    useState<ValidationResult<MaintenanceTask> | null>(null);
  const [localMaintenanceTask, setLocalMaintenanceTask] = useState<
    Partial<NewMaintenanceTaskInput>
  >(
    initialMaintenanceTask || {
      assetType,
      assetId,
    }
  );
  const [maintenanceTaskType, setMaintenanceTaskType] =
    useState<MaintenanceTaskType | null>(
      localMaintenanceTask.startMileage
        ? MaintenanceTaskType.MILEAGE
        : MaintenanceTaskType.DATE
    );

  const getFieldError = (field: string, partialPathMatch = false) =>
    validationResult?.error?.details.find((error) =>
      partialPathMatch
        ? error.path.join(".").startsWith(field)
        : error.path.join(".") === field
    )?.message;

  const onChange = useCallback((changes: DeepPartialMaintenanceTask) => {
    setLocalMaintenanceTask((localMaintenanceTask) =>
      mergeWith({}, localMaintenanceTask, changes, (objValue, srcValue) => {
        if (isArray(srcValue)) {
          return srcValue;
        }
      })
    );
  }, []);

  const validate = () => {
    const validationResult = maintenanceTaskSchema.validate(
      omit(localMaintenanceTask, ["_id"]),
      {
        abortEarly: false,
      }
    );
    setValidationResult(validationResult);
    return !validationResult.error;
  };

  useEffect(() => {
    if (validationResult) {
      validate();
    }
    // We don't want to run everytime validationResult changes
    // otherwise we ill have an infinite update loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localMaintenanceTask]);

  const { cancelConfirm } = useConfirmBeforeLeave(localMaintenanceTask);

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item sm={12}>
          <FormDivider variant="fullWidth" text="Maintenance Task Details" />
          <ErrorMessage message={validationResult?.error?.message} />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label={t("common:label", "Label")}
                  fullWidth
                  required
                  value={localMaintenanceTask.label || ""}
                  error={!!getFieldError("label")}
                  helperText={getFieldError("label")}
                  onChange={(event) => {
                    onChange({ label: event.target.value });
                  }}
                inputProps={{ "data-testid": testIds.maintenanceTaskFormLabel } as React.InputHTMLAttributes<HTMLInputElement>}
                />
                <FormControlLabel
                  label={"Required"}
                  control={
                    <Checkbox
                      checked={localMaintenanceTask.required || false}
                      onChange={(event, checked) => {
                        onChange({ required: checked });
                      }}
                    inputProps={{ "data-testid": testIds.maintenanceTaskFormRequired } as React.InputHTMLAttributes<HTMLInputElement>}
                    />
                  }
                />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={3}>
              <EnumSelect data-testid="maintenance-task-form-maintenance-task-type"
                enumObject={MaintenanceTaskType}
                label={"Type"}
                fullWidth
                value={maintenanceTaskType || null}
                error={!!getFieldError("type")}
                helperText={getFieldError("type")}
                onChange={(event, value) => {
                  setMaintenanceTaskType(value);
                  onChange({
                    startMileage: undefined,
                    startDate: undefined,
                    recurrence: null,
                  });
                }}
              />
            </Grid>
            {maintenanceTaskType === MaintenanceTaskType.DATE ? (
              <>
                <Grid item xs={12} sm={4.5}>
                  <DateTimePicker
                    label="Start date"
                    ampm={false}
                    value={localMaintenanceTask?.startDate || null}
                    onChange={(date) => {
                      onChange({ startDate: date });
                    }}
                    maxDate={localMaintenanceTask?.endDate || null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        fullWidth
                        error={!!getFieldError("startDate")}
                        helperText={getFieldError("startDate")}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4.5}>
                  <DateTimePicker
                    label="End date"
                    ampm={false}
                    value={localMaintenanceTask?.endDate || null}
                    onChange={(date) => {
                      onChange({ endDate: date });
                    }}
                    minDate={localMaintenanceTask?.startDate || null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!getFieldError("endDate")}
                        helperText={getFieldError("endDate")}
                      />
                    )}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={9}>
                <MileageInput
                  value={
                    localMaintenanceTask.startMileage
                      ? localMaintenanceTask.startMileage
                      : ""
                  }
                  onChange={(mileage) => {
                    if (!mileage) {
                      onChange({ startMileage: null });
                      return;
                    }
                    onChange({
                      startMileage: parseFloat(String(mileage)),
                    });
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                label={t("common:recurrent", "Recurrent")}
                control={
                  <Checkbox
                    checked={!!localMaintenanceTask.recurrence}
                    onChange={(event, checked) => {
                      if (checked) {
                        if (maintenanceTaskType === MaintenanceTaskType.DATE) {
                          onChange({
                            recurrence: {
                              frequency:
                                MaintenanceTaskRecurrenceFrequency.Daily,
                              interval: 1,
                            },
                          });
                        } else if (
                          maintenanceTaskType === MaintenanceTaskType.MILEAGE
                        ) {
                          onChange({
                            recurrence: {
                              frequency:
                                MaintenanceTaskRecurrenceFrequency.Miles,
                              interval: METERS_PER_MILE * 100, // 100 miles in meters
                            },
                          });
                        }
                      } else {
                        onChange({ recurrence: null });
                      }
                    }}
                  inputProps={{ "data-testid": testIds.maintenanceTaskFormRecurring } as React.InputHTMLAttributes<HTMLInputElement>}
                  />
                }
              />
              {!!localMaintenanceTask.recurrence ? (
                <Stack direction="row" spacing={2}>
                  {maintenanceTaskType === MaintenanceTaskType.DATE ? (
                    <>
                      <NumberTextField
                        label="Every"
                        placeholder="1"
                        value={localMaintenanceTask.recurrence?.interval || ""}
                        onChange={(event) => {
                          onChange({
                            recurrence: {
                              interval: parseInt(event.target.value),
                            },
                          });
                        }}
                        error={!!getFieldError("recurrence.interval")}
                        helperText={getFieldError("recurrence")}
                      />

                      <EnumSelect data-testid="maintenance-task-form-mileage-interval"
                        enumObject={omitBy(
                          MaintenanceTaskRecurrenceFrequency,
                          (v) => v === MaintenanceTaskRecurrenceFrequency.Miles
                        )}
                        label={"Recurrence"}
                        fullWidth
                        withNoneOption
                        value={
                          localMaintenanceTask.recurrence?.frequency || null
                        }
                        error={!!getFieldError("recurrence.frequency")}
                        helperText={getFieldError("recurrence.frequency")}
                        optionLabel={
                          maintenanceTaskRecurrenceFrequencyUnitLabel
                        }
                        onChange={(event, value) => {
                          if (!value) {
                            onChange({ recurrence: null });
                            return;
                          }
                          onChange({
                            recurrence: {
                              frequency: value,
                              interval:
                                localMaintenanceTask.recurrence?.interval || 1,
                            },
                          });
                        }}
                      />
                    </>
                  ) : null}
                  {maintenanceTaskType === MaintenanceTaskType.MILEAGE ? (
                    <MileageInput
                      label="Every"
                      value={localMaintenanceTask.recurrence?.interval || ""}
                      onChange={(value) => {
                        onChange({
                          recurrence: {
                            interval: parseFloat(String(value)),
                            frequency: MaintenanceTaskRecurrenceFrequency.Miles,
                          },
                        });
                      }}
                    />
                  ) : null}
                </Stack>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row-reverse",
          pt: 3,
        }}
      >
        <Button data-testid={testIds.maintenanceTaskFormSave}
          variant="contained"
          disabled={saving || !!validationResult?.error}
          size="large"
          onClick={() => {
            if (!localMaintenanceTask) {
              return;
            }
            if (validate()) {
              cancelConfirm();
              onSave(localMaintenanceTask as NewMaintenanceTaskInput);
            }
          }}
          id="saveMaintenanceTaskButton"
        >
          {t("common:save", "Save")}
        </Button>
      </Box>
    </Box>
  );
};

export default MaintenanceTaskForm;

const maintenanceTaskRecurrenceFrequencyUnitLabel = (
  value: MaintenanceTaskRecurrenceFrequency
): string => {
  switch (value) {
    case MaintenanceTaskRecurrenceFrequency.Daily:
      return i18next.t(`common:days`, "Days");
    case MaintenanceTaskRecurrenceFrequency.Weekly:
      return i18next.t(`common:weeks`, "Weeks");
    case MaintenanceTaskRecurrenceFrequency.Monthly:
      return i18next.t(`common:months`, "Months");
    case MaintenanceTaskRecurrenceFrequency.Yearly:
      return i18next.t(`common:years`, "Years");
    case MaintenanceTaskRecurrenceFrequency.Miles:
      return i18next.t(`common:miles`, "Miles");
    default:
      return value;
  }
};
